import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import { IObjectDashAdminState } from './ObjectDashboard/IObjectDashAdminState';
import { ObjDashAdminInitial } from './ObjectDashboard/ObjDashAdminInitial';
import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';
import ObjectDashboard from '@/types/Dashboard/ObjectDashboard/ObjectDashboard';
import ObjectWidget from '@/types/Dashboard/ObjectDashboard/ObjectWidget';
import BaseTreeNode from '@/types/BaseTreeNode';
import SaveModeEnum from '@/enums/SaveModeEnum';
import Pin from '@/types/FilterTree/Pin';

/** VueX pour gérer la persistance des données entre ObjectDashboardStore et ses sous-composants */
@Module({
	namespaced: true
})
class ObjectDashboardStore extends VuexModule {
//#region Data
	private loadState!: IObjectDashAdminState ;

	/** Arborescence de type d'objet et de dashboard*/
	private adminTree: BaseTreeNode[] = [] as BaseTreeNode[];

	/** Noeud en cour de selection dans l'arborescence */
	private dashNode: BaseTreeNode = {} as BaseTreeNode;

	/** Tableau de bord actuellement sélectionné */
	private objectDashboard: ObjectDashboard = {
		Id: -1,
		Name: '',
		Widgets: [] as ObjectWidget[],
		ObjectType: BaseObjectTypeEnum.Root,
		ProfileIds: [] as number[],
		Pins: [] as Pin[]
	};

	/** Tableau de bord est modifié ?  */
	private isModified: boolean = false;

	/** Mode de sauvegarde du tableau de bord */
	private saveMode: SaveModeEnum = SaveModeEnum.UpdateObject;

	/** Dernier Id temporaire de widget ou autre (décrémentée par mutation à chaque nouvel id temporaire) */
	private tempObjectId: number = 0;

//#endregion Data
//#region Mutations
	/** Réinitialisation store VueX */
	@Mutation
	private reset() {
		this.objectDashboard = {
			Id: -1,
			Name: '',
			Widgets: [],
			ObjectType: BaseObjectTypeEnum.Root,
			ProfileIds: [],
			Pins: []
		} as ObjectDashboard;
		this.saveMode = SaveModeEnum.UpdateObject;
		this.adminTree = [] as BaseTreeNode[];
		this.dashNode = {} as BaseTreeNode;
		this.tempObjectId = 0;
	}

	/** Applique un mode de sauvegarde du dashboard */
	@Mutation
	private setSaveMode({ saveMode }) {
		this.saveMode = saveMode;
	}

	/** Definit l'arbre d'objet type et de tableau de bord (AdminTree) */
	@Mutation
	private loadTree(adminTree: BaseTreeNode[]) {
		this.adminTree = adminTree;
	}

	/** Définit le noeud en cours de selection  */
	@Mutation
	private setDashNode(newDashNode: BaseTreeNode) {
		this.dashNode = newDashNode;
	}

	/** Définit le tableau de bord en cours */
	@Mutation
	private setObjectDash(newObjectDash: ObjectDashboard) {
		console.debug("ObjectDashboardStore - setObjectDash(newObjectDash: ObjectDashboard) - newObjectDash.Id : " + newObjectDash.Id + " - newObjectDash.Name : " + newObjectDash.Name);
		this.objectDashboard = newObjectDash;
		console.debug("ObjectDashboardStore - setObjectDash(newObjectDash: ObjectDashboard) - this.objectDashboard.Id : " + this.objectDashboard.Id + " - this.objectDashboard.Name : " + this.objectDashboard.Name);
	}

	/** Ajoute un widget sur le tableau de bord en cours */
	@Mutation
	private setAddWidget(widget: ObjectWidget) {
		this.objectDashboard.Widgets.push(widget);
	}

	/** Retire un widget du tableau de bord en cours */
	@Mutation
	private setRemoveWidget(widgetId) {
		let index = this.objectDashboard.Widgets.findIndex(x => x.i == widgetId);
		this.objectDashboard.Widgets.splice(index, 1);
	}

	@Mutation
	private updateWidget({ dashboardId, widgetId, datas }) {
		console.debug("ObjectDashboardStore - updateWidget() - {dashboardId: %O, widgetId: %O, Datas: %O} ", dashboardId, widgetId, datas);

		let indexWidget: number = this.objectDashboard.Widgets.findIndex(x => x.Id == widgetId);

		if (indexWidget > -1) {
			// Pour chaque paire clé-valeur, on met à jour la propriété sur le widget
			for(let key in datas)
				Vue.set(this.objectDashboard.Widgets[indexWidget], key, datas[key]);
		}
		console.debug("ObjectDashboardStore - updateWidget() - dashboard ", this.objectDashboard);
	}

	/** Décrémente le compteur de widget temporaire */
	@Mutation
	private setTempObjectId () {
		--this.tempObjectId;
	}

	/** Machine à états utilisée pour gérer les états de chargement et la sélection du store. */
	@Mutation
	private setLoadState(next: IObjectDashAdminState){
		this.loadState = next;
	}

	/** Indique que le tableau est modifié */
	@Mutation
	private setIsModified(isModified: boolean){
		this.isModified = isModified;
	}
//#endregion Mutations
//#region Actions

	/** Réinitialisation du store VueX */
	@Action({ rawError: true })
	public Reset() {
		this.context.commit('reset');
		this.context.commit('setLoadState', new ObjDashAdminInitial(this));
	}

	/** Met un dashboard vide comme dashboard en cours */
	@Action({ rawError: true })
	public SetEmptyDashboard() {
		this.loadState.Clear();
	}

	/** **** TREE **** */

	/** Récupération de l'arborescence de type d'objet avec les dashboards existants */
	@Action({ rawError: true })
	public async LoadTree(nodeIdToSelect = "") {
		if(!this.loadState)
			this.context.commit('setLoadState', new ObjDashAdminInitial(this));
		await this.loadState.LoadTree(nodeIdToSelect);
	}

	/** **** DASHBAORD **** */

	/**
	 * Céer un dashboard annexe à partir d'un noeud de l'arborescence
	 * @param dashNode Noeud type père de ce tableau de bord
	 * @param profileIds Profiles utilisateurs attaché à ce tableau de bord
	 * @param pins Pins de l'arborescence à sauvegarder
	 * @param widgets Widget présent dans ce tableau de bord
	 */
	@Action({ rawError: true})
	public async CreateDashboard({ dashNode, profileIds = [], pins = [], widgets = [], }) {
		await this.loadState.CreateDashboard(dashNode, profileIds, pins, widgets)
	}

	/**
	 * Selectionne le un dashboard via un noeud dans l'arborescence
	 * @param dashNode Noeud de type présent dans l'aborescence de type de noeud
	 */
	@Action({ rawError: true })
	public async SelectDashNode(dashNode: BaseTreeNode) {
		await this.loadState.SelectDashNode(dashNode)
	}

	/** Supprime un dashboard */
	@Action({ rawError: true })
	public async Delete(dashNode: BaseTreeNode) {
		await this.loadState.RemoveDashboard(dashNode)
	}

	/** 
	 * Enregistrement du dashboard
	 * @param dashboard Dashboard à sauvegarder 
	 * @returns response axios du save
	 */
	@Action({ rawError: true })
	public async Save({ dashboard }) {
		console.debug("ObjectDashboardStore - Save() - dashboard", dashboard);
		await this.loadState.SaveDashboard(dashboard);
	}
	
	/** **** WIDGETS **** */
	
	/**
	 * Permet d'obtenir un Id de widget temporaire avant qu'il soit effectivement enregistré dans le back
	 */
	@Action({ rawError: true })
	public SetTempObjectId() {
		this.context.commit('setTempObjectId');
	}

	@Action({ rawError: true })
	public SetAddWidget(widget: ObjectWidget) {
		this.loadState.AddWidget(widget);
	}

	@Action({ rawError: true })
	public SetRemoveWidget(widgetId: number) {
		this.loadState.RemoveWidget(widgetId);
	}

	/**
	 * Mise à jour d'un widget sur un tableau de bord (Pourquoi utiliser ça dans la fiche objet ?)
	 * @param dashboardId Id du tableau de bord
	 * @param widgetId	Identifiant du widget
	 * @param nom
	 * @param parameters paramètres du widget à mettre à jour
	 */
	@Action({ rawError: true })
	public UpdateWidgetParameters({ dashboardId, widgetId, name = undefined, parameters }) {
		let datas = { Parameters: parameters };
		if (name)
			datas['Name'] = name;

		this.context.commit('updateWidget', { dashboardId, widgetId, name, datas: datas }) ;
	}

	/** 
	 * Mise à jour d'un widget sur un tableau de bord (Pourquoi utiliser ça dans la fiche objet ?)
	 * @param dashboardId Id du tableau de bord
	 * @param widgetId	Identifiant du widget
	 * @param datas Données du widget à mettre à jour
	 **/
	@Action({ rawError: true })
	public UpdateWidget({ dashboardId, widgetId, datas }) {
		console.debug("ObjectDashboardStore - UpdateWidget() - {dashboardId: %O, widgetId: %O, Datas: %O ", dashboardId, widgetId, datas);
		this.context.commit('updateWidget', { dashboardId, widgetId, datas }) ;
	}

	@Action({ rawError: true })
	public SetIsModified(isModified: boolean = true){
		this.context.commit('setIsModified', isModified);
	}
	
//#endregion Actions
//#region Getters

	/** Renvoi l'aborescence 
	 * 		- Nœud type: nœud désignant un tableau de bord général à ce type de patrimoine
	 * 		- Nœud dashbord: nœud désignant un tableau de bord spécifique lié à un profil utilisateur ou un nœud du patrimoine client ou les deux
	 */
	public get GetAdminTree(): BaseTreeNode[] {
		return this.adminTree;
	}

	/** Renvoi le noeud sélectionné dans l'arborescence */
	public get GetDashNode(): BaseTreeNode {
		return this.dashNode;
	}

	/** Renvoi le tableau de bord sélectionné */
	public get GetObjectDash(): ObjectDashboard {
		return this.objectDashboard;
	}

	/** Récupère la liste des widget du tableau de bord en cours de selection */
	public get GetWidgets(): ObjectWidget[] {
		return this.objectDashboard.Widgets;
	}

	/** Renvoi le dernier WidgetId temporaire disponible */
	public get GetTempObjectId(): number {
		return this.tempObjectId;
	}

	public get GetSaveMode(): SaveModeEnum {
		return this.saveMode;
	}

	/** Indique si le tableau de bord a été modifié */
	public get GetIsModified(): boolean{
		return this.isModified;
	}

//#endregion Getters
}

//#region Fonctions locales
function searchNode(idToSearch: string, nodes: BaseTreeNode[]): BaseTreeNode | null {
	for(let node of nodes) {
		if (node.Id == idToSearch) 
			return node;
		else if(node.Children && node.Children.length > 0) {
			let childNode: BaseTreeNode | null = searchNode(idToSearch, node.Children);
			if (childNode)
				return childNode;
		}
	}
	return null;
}
//#endregion Fonctions locales

export default ObjectDashboardStore;